import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSessionProvider } from "../../provider/SessionProvider";

interface TimerProps {
  onExpire?: () => void;
}

const Timer: React.FC<TimerProps> = ({ onExpire }) => {
  const { sessionDuration, timeLeftRef, setIsOpen } = useSessionProvider();
  const [displayTime, setDisplayTime] = useState(sessionDuration);
  const warningShownRef = useRef(false);

  const startTime = useMemo(() => {
    const stored = sessionStorage.getItem("timerStartTime");
    if (stored) return parseInt(stored);
    const now = Date.now();
    sessionStorage.setItem("timerStartTime", now.toString());
    return now;
  }, []);

  useEffect(() => {
    // Declared ahead of updateTimer, because updateTimer runs synchronously
    // below and has to be able to clear the interval from inside that first
    // call. This used to be a `const` declared *after* the call, so a timer that
    // was already expired on mount read it inside its temporal dead zone and
    // threw a ReferenceError instead of expiring — stranding the applicant on
    // the capture screen with a dead clock, because onExpire never ran.
    let intervalId: ReturnType<typeof setInterval> | undefined;
    let expired = false;

    const updateTimer = () => {
      const elapsedTime = Math.floor((Date.now() - startTime) / 1000);
      const newTimeLeft = sessionDuration - elapsedTime;

      timeLeftRef.current = newTimeLeft;

      if (newTimeLeft <= 0) {
        expired = true;
        setDisplayTime(0);
        // Cleared unconditionally, not only when onExpire is supplied: without a
        // callback the interval would otherwise keep ticking against a spent
        // clock for as long as the component stayed mounted.
        clearInterval(intervalId);
        onExpire?.();
        return;
      }

      if (newTimeLeft == 300 && !warningShownRef.current) {
        setIsOpen(true); // trigger SessionWarningModal
        warningShownRef.current = true;
      }

      setDisplayTime(newTimeLeft); // this only updates formatted display, not full context
    };

    updateTimer();
    // Nothing to arm if that first call already found the clock spent.
    if (!expired) intervalId = setInterval(updateTimer, 1000);
    return () => clearInterval(intervalId);
  }, [onExpire, sessionDuration, setIsOpen, startTime]);

  const formattedTime = useMemo(() => {
    const minutes = Math.floor(displayTime / 60).toString().padStart(2, "0");
    const seconds = (displayTime % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  }, [displayTime]);

  return <>{formattedTime}</>;
};

export default React.memo(Timer);